/**
 * The screen's half of an answer. Story VC-016.
 *
 * `crates/agents-example/src/poc.rs` is the contract this repository ships:
 * `display_string` is what is said, `visual_fragments` is what is shown, and
 * `next_actions` is what to offer next (PC-006).
 *
 * An application is expected to spell those fields identically, and
 * Apex-Concierge does — "one rendering policy and one client renderer serve
 * both agents", which is this file. That the agreement is now across two
 * repositories rather than two crates is exactly the duplication RS-020
 * records: nothing compiles both any more, and this comment is what says so.
 *
 * The screen fields are `display_only` in the server's rendering policy
 * (`voice_server_lld.md` §25), so they reach the client and the speech
 * provider not at all. That is why a fragment may safely hold a URL nobody
 * wants read aloud, and why the pills are not announced.
 *
 * # Everything here is a string a model wrote
 *
 * `textContent` throughout and `src` assigned rather than interpolated —
 * which in React means never `dangerouslySetInnerHTML`, and an `<img src>`
 * bound as a prop. Building markup from a response would make an answer an
 * injection vector.
 */

import { Icon } from "./Icon.jsx";

/**
 * The fragments, as a row that scrolls sideways.
 *
 * Horizontal because the count is the model's: three shipping options and
 * eleven appointments are the same component, and a grid that wrapped would
 * reflow the whole conversation when a fourth arrived.
 *
 * @param {{ fragments: any[] }} props
 */
export function Fragments({ fragments }) {
  if (!Array.isArray(fragments) || fragments.length === 0) {
    return null;
  }
  return (
    <div
      className="fragments"
      role="list"
      // Reachable by keyboard, because a scroll container with no focusable
      // child is a region a keyboard user cannot scroll.
      tabIndex={0}
      aria-label="Details on screen"
    >
      {fragments.map((fragment, index) => (
        <Fragment key={index} fragment={fragment} />
      ))}
    </div>
  );
}

/** @param {{ fragment: any }} props */
function Fragment({ fragment }) {
  const full = fragment?.fragment_type === "full_image";
  const image =
    typeof fragment?.image_url === "string" && fragment.image_url
      ? fragment.image_url
      : null;
  const subtitles = ["subtitle_1", "subtitle_2"]
    .map((field) => fragment?.[field])
    .filter((value) => typeof value === "string" && value);

  return (
    <article
      className={full ? "fragment fragment--full-image" : "fragment"}
      role="listitem"
    >
      {image ? (
        <img
          className="fragment__image"
          // A prop, never markup. A model wrote this string.
          src={image}
          alt={typeof fragment?.title === "string" ? fragment.title : ""}
          loading="lazy"
        />
      ) : null}
      <div className={full ? "fragment__text" : undefined}>
        {typeof fragment?.title === "string" && fragment.title ? (
          <div className="fragment__title">{fragment.title}</div>
        ) : null}
        {subtitles.map((line, index) => (
          <div className="fragment__subtitle" key={index}>
            {line}
          </div>
        ))}
      </div>
    </article>
  );
}

/**
 * The next-action pills. PC-006.
 *
 * A pill is a sentence the user could have said, so choosing one *sends*
 * that sentence — it does not carry an id and it is not a command. That is
 * the contract's own decision and the reason the strings read as speech:
 * the moment a pill had an id, this client would be deciding what the agent
 * meant.
 *
 * Disabled while a turn is running rather than hidden. Hiding them would
 * move the composer under the user's thumb mid-tap, and a pill that does
 * nothing for two seconds is better than a layout that jumps.
 *
 * @param {{ actions: any[], onChoose: (text: string) => void,
 *           disabled?: boolean }} props
 */
export function NextActions({ actions, onChoose, disabled = false }) {
  const pills = (Array.isArray(actions) ? actions : []).filter(
    (action) => typeof action === "string" && action.trim(),
  );
  if (pills.length === 0) {
    return null;
  }
  return (
    <div className="next-actions" role="group" aria-label="Suggested replies">
      {pills.map((action, index) => (
        <button
          type="button"
          key={`${index}:${action}`}
          className="chip chip--suggested"
          disabled={disabled}
          onClick={() => onChoose(action)}
        >
          {action}
        </button>
      ))}
    </div>
  );
}

/**
 * The whole screen payload for one response snapshot.
 *
 * A snapshot whose shape this client does not know renders as itself. A
 * deployment with its own response model still sees what arrived, which is
 * the difference between "this build does not render your contract" and a
 * blank panel.
 *
 * @param {{ snapshot: any, onChoose: (text: string) => void,
 *           busy?: boolean }} props
 */
export function AnswerScreen({ snapshot, onChoose, busy = false }) {
  if (!snapshot || typeof snapshot !== "object") {
    return null;
  }

  const known =
    "display_string" in snapshot ||
    "visual_fragments" in snapshot ||
    "next_actions" in snapshot;
  if (!known) {
    return (
      <details className="turn__unknown">
        <summary className="turn__interrupted">
          <Icon name="warning" size={14} /> a response this client does not
          render
        </summary>
        <pre className="raw-snapshot">{JSON.stringify(snapshot, null, 2)}</pre>
      </details>
    );
  }

  return (
    <>
      <Fragments fragments={snapshot.visual_fragments} />
      <NextActions
        actions={snapshot.next_actions}
        onChoose={onChoose}
        disabled={busy}
      />
    </>
  );
}
